import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { NavigatorWithUserLanguage } from '../models/i18n.model';
import { GLOBAL_CONSTANT } from '../constants/global.constant';
import { I18N_STRINGS_IA } from '../i18n/strings_ia';
import { I18N_LEGALS_IA } from '../i18n/legals_ia';
import { I18N_STRINGS_EM } from '../i18n/strings_em';
import { I18N_LEGALS_EM } from '../i18n/legals_em';
import { EnumUtil } from '../utilities/enum.util';
import { SupportedLanguage } from '../enums/i18.enum';
import { LP_UI_CONFIG } from '../core/tokens';
import { SettingsStore } from './settings.store';
import { SettingsUtil } from '../utilities/settings.util';

@Injectable({ providedIn: 'root' })
export class I18nStore {
  private readonly cfg = inject(LP_UI_CONFIG);
  private readonly settingsStore = inject(SettingsStore);

  // ==================== Private Mutable Signals ====================
  private _language = signal<string>(this.getBrowserLanguage());
  private _measurementSystem = signal<string>('mt');
  private _languageFetched = signal<string | null>(null);

  // ==================== Public Readonly Signals ====================
  readonly language = this._language.asReadonly();
  readonly measurementSystem = this._measurementSystem.asReadonly();
  readonly languageFetched = this._languageFetched.asReadonly();

  // ==================== Computed Signals ====================

  /**
   * Current language abbreviation (backward compatibility as 'Lng')
   */
  readonly lng = computed(() => this._language());

  /**
   * Current measurement system object (for pipes and components)
   */
  readonly currentMeasurementSystem = computed(() => {
    const settings = this.settingsStore.userSettings();
    if (!settings) return undefined;
    return SettingsUtil.getMeasurementSystem(
      settings.measurementSystems,
      settings.measurementSystemID
    );
  });

  /**
   * Measurement system in pipe-compatible format ('im' for imperial, 'si' for metric)
   * Maps 'imp' -> 'im', 'mt' -> 'si'
   */
  readonly pipeCompatibleMeasurementSystem = computed<'im' | 'si'>(() => {
    const measurementSystem = this._measurementSystem();

    return measurementSystem === 'im' ? 'im' : 'si';
  });

  /**
   * Translation source based on app name and current language
   */
  private readonly translationSource = computed(() => {
    if (this.cfg.appName === 'ia') {
      return { strings: I18N_STRINGS_IA, legals: I18N_LEGALS_IA };
    }
    if (this.cfg.appName === 'em') {
      return { strings: I18N_STRINGS_EM, legals: I18N_LEGALS_EM };
    }
    return { strings: I18N_STRINGS_IA, legals: I18N_LEGALS_IA }; // default
  });

  constructor() {
    // Sync language and measurement system with settings store
    effect(() => {
      const settings = this.settingsStore.userSettings();
      if (settings) {
        const lang = SettingsUtil.getLanguage(
          settings.languages,
          settings.languageID
        )?.abbreviation;
        const measurement = SettingsUtil.getMeasurementSystem(
          settings.measurementSystems,
          settings.measurementSystemID
        )?.abbreviation;

        if (lang && lang !== this._language()) {
          this._language.set(lang);
          this._languageFetched.set(lang);
        }
        if (measurement && measurement !== this._measurementSystem()) {
          this._measurementSystem.set(measurement);
        }
      }
    });
  }

  // ==================== Actions ====================

  /**
   * Set the language
   * @param lang - Language code to set
   */
  setLanguage(lang: string): void {
    if (EnumUtil.hasValue(SupportedLanguage, lang)) {
      this._language.set(lang);
      this._languageFetched.set(lang);
    } else {
      console.warn(`Invalid language: ${lang}. Using default.`);
      this._language.set(SupportedLanguage.EN);
    }
  }

  /**
   * Set the measurement system
   * @param system - Measurement system abbreviation (e.g., 'mt', 'imp')
   */
  setMeasurementSystem(system: string): void {
    this._measurementSystem.set(system);
  }

  /**
   * Mark language as fetched (used for initialization tracking)
   * @param lang - Language code that was fetched
   */
  setLanguageFetched(lang: string): void {
    this._languageFetched.set(lang);
  }

  // ==================== Translation Methods ====================

  /**
   * Translate a key to the current language
   * @param key - Translation key
   * @returns Translated string or empty string if not found
   */
  translate(key: string): string {
    const lang = this._language();
    const source = this.translationSource();
    const combinedSource = { ...source.strings[key], ...source.legals[key] };

    if (combinedSource && EnumUtil.hasValue(SupportedLanguage, lang)) {
      const translation = combinedSource[lang as keyof typeof combinedSource];
      if (translation) {
        return translation;
      }
    }

    console.error(`Unable to find translation for key: '${key}'`);
    return '';
  }

  /**
   * Parse localized content based on current language and measurement system
   * @param source - Content to parse (can be object or string)
   * @returns Parsed string
   */
  parse(source: any): string {
    try {
      source = typeof source === 'string' ? JSON.parse(source) : source;
    } catch {
      // Not JSON, continue
    }

    if (source == null) return '';
    if (typeof source === 'string') return source;
    if (typeof source === 'number') return source.toString();

    const lang = this._language();
    const locale = this.cfg.locale;
    const system = this._measurementSystem();

    return (
      source?.[lang]?.[system] ??
      source?.[locale]?.[system] ??
      source?.[lang] ??
      source?.[locale] ??
      ''
    );
  }

  // ==================== Private Helpers ====================

  /**
   * Get browser's default language
   * @returns Language code or default to 'en'
   */
  private getBrowserLanguage(): string {
    let lang =
      navigator.language ||
      (navigator as NavigatorWithUserLanguage).userLanguage ||
      GLOBAL_CONSTANT.EMPTY_STRING;

    lang = lang
      ? lang.slice(GLOBAL_CONSTANT.DEFAULT_INITIALIZER, GLOBAL_CONSTANT.LANG_SLICE_NUMBER)
      : SupportedLanguage.EN;

    return EnumUtil.hasValue(SupportedLanguage, lang) ? lang : SupportedLanguage.EN;
  }

  /**
   * Clear all state
   */
  clear(): void {
    this._language.set(this.getBrowserLanguage());
    this._measurementSystem.set('mt');
    this._languageFetched.set(null);
  }
}
